import axios from 'axios'
import type { LoginCredentials, AuthResponse, User } from '@/types'

// Função para obter URL da API - usa window.ENV se disponível, senão fallback para import.meta.env
const getApiUrl = () => {
  // @ts-ignore
  if (typeof window !== 'undefined' && (window as any).ENV?.VITE_API_URL) {
    // @ts-ignore
    return (window as any).ENV.VITE_API_URL
  }
  
  const envUrl = import.meta.env.VITE_API_URL
  const fallbackUrl = import.meta.env.DEV ? '/api' : 'http://localhost:5003'
  const finalUrl = envUrl || fallbackUrl
  
  return finalUrl
}

const API_BASE_URL = getApiUrl()

// Configuração do axios
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Interceptor para adicionar token de autenticação
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Interceptor para tratar erros de autenticação
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      // Limpar dados de autenticação apenas se não for erro de login
      if (error.config?.url !== '/login') {
        localStorage.removeItem('auth_token')
        localStorage.removeItem('user_data')
        
        // Redirecionar para login apenas se não estiver já na página de login
        if (window.location.pathname !== '/login' && window.location.pathname !== '/privacy-policy') {
          window.location.href = '/login'
        }
      }
    }
    return Promise.reject(error)
  }
)

// Serviços de autenticação
export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await api.post('/login', credentials)
    return response.data
  },

  async getCurrentUser(): Promise<User> {
    const userData = this.getUser()
    if (userData) {
      return userData
    }
    throw new Error('Usuário não encontrado')
  },

  // Função de validação de token removida temporariamente para resolver problemas de login
  async validateToken(): Promise<boolean> {
    // Por enquanto, sempre retorna true para não interferir no login
    return true
  },

  setToken(token: string) {
    localStorage.setItem('auth_token', token)
  },

  getToken(): string | null {
    return localStorage.getItem('auth_token')
  },

  removeToken() {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user_data')
  },

  setUser(user: User) {
    localStorage.setItem('user_data', JSON.stringify(user))
  },

  getUser(): User | null {
    const userData = localStorage.getItem('user_data')
    return userData ? JSON.parse(userData) : null
  },

  isAuthenticated(): boolean {
    return !!this.getToken() && !!this.getUser()
  },
}

export { api }
export default api
